import React from "react";
import { Handshake, Palette, Code2, LineChart } from "lucide-react";

const SERVICES = [
  {
    id: "advisory",
    icon: Handshake,
    eyebrow: "01 — Advisory",
    title: "Advisory with a delivery mandate.",
    body:
      "We work inside your strategy, capital and operating decisions — helping you size the opportunity, pick the right technology and partners, structure offtake and financing, and see the build through to commissioning.",
    items: [
      "Opportunity sizing & market entry",
      "Feasibility, DD and independent engineering",
      "Technology & vendor selection",
      "PPA / offtake & regulatory structuring",
      "ESG & carbon architecture",
      "Programme & delivery oversight",
    ],
  },
  {
    id: "software-design",
    icon: Palette,
    eyebrow: "02 — Software design",
    title: "Software designed around the operating reality.",
    body:
      "We design the software layer that industrial and energy businesses actually use — from architecture and data models to UX and integration contracts. Built with the operators who will run it, not just the people who bought it.",
    items: [
      "Product & platform architecture",
      "Data models & APIs",
      "UX for operations teams",
      "Integration & interoperability design",
      "Security & compliance-by-design",
      "Build-vs-buy decisioning",
    ],
  },
  {
    id: "software-implementation",
    icon: Code2,
    eyebrow: "03 — Software implementation",
    title: "We ship the software, then keep it running.",
    body:
      "Implementation teams that deliver. ERP, asset-management, SCADA/IoT, analytics and workflow platforms — engineered, integrated, commissioned and supported — with accountability for adoption, uptime and outcomes.",
    items: [
      "ERP & asset-management rollouts",
      "SCADA / IoT & plant integration",
      "Data & analytics platforms",
      "Workflow & automation",
      "Migration & go-live support",
      "Managed operations",
    ],
  },
  {
    id: "investment",
    icon: LineChart,
    eyebrow: "04 — Investment",
    title: "Operator-grade capital.",
    body:
      "Saafi invests from its own balance sheet and alongside aligned partners into companies and assets building the industrial backbone of the transition — renewables, infrastructure and the software that runs them.",
    items: [
      "Growth equity for operators",
      "Asset-level project capital",
      "Structured & mezzanine positions",
      "Co-investment with LPs & DFIs",
      "Platform roll-ups",
      "Early-stage, where execution risk dominates",
    ],
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-20">
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#79836B] mb-6">
          Services
        </p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tighter font-medium max-w-5xl">
          Four capabilities. <br /> One <em className="not-italic text-[#0F7A3E]">operating</em> commitment.
        </h1>
        <p className="mt-10 max-w-2xl text-lg text-[#575B57] leading-relaxed">
          Our services are modular — and most of our best work blends them.
          We advise where decisions get made, design and implement the software
          the operation runs on, and invest where our thesis is strong.
        </p>
      </section>

      {/* Services list */}
      <section className="border-t border-[#E4E5DF]">
        {SERVICES.map((s, idx) => {
          const Icon = s.icon;
          return (
            <div
              key={s.id}
              id={s.id}
              data-testid={`service-row-${s.id}`}
              className={`border-b border-[#E4E5DF] ${idx % 2 === 0 ? "bg-[#FBFBF9]" : "bg-[#F3F3E8]/50"}`}
            >
              <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10">
                <div className="md:col-span-4">
                  <Icon className="w-8 h-8 text-[#0F7A3E] mb-10" strokeWidth={1.25} />
                  <p className="text-[11px] uppercase tracking-[0.25em] text-[#79836B] mb-4">
                    {s.eyebrow}
                  </p>
                  <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight font-medium">
                    {s.title}
                  </h2>
                </div>
                <div className="md:col-span-7 md:col-start-6">
                  <p className="text-lg leading-relaxed text-[#575B57] max-w-2xl">
                    {s.body}
                  </p>
                  <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4">
                    {s.items.map((it) => (
                      <li key={it} className="flex items-start gap-3 text-[15px] text-[#1A1C1A] border-t border-[#E4E5DF] pt-4">
                        <span className="inline-block w-1.5 h-1.5 mt-2 bg-[#A3C940]" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
}
